/**
 * @file apps/web/src/pages/ProjectsPage.tsx
 * @description Projects list page. Fetches and displays all projects for the current tenant.
 * Supports creating new projects via a form panel.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, AlertCircle, Calendar, Building2, X } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { projectsApi, clientsApi } from '../lib/api.js';
import { Badge } from '../components/Badge.js';
import { ERP_SYSTEMS, type ErpSystem } from '@veridox/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Project {
  id:            string;
  name:          string;
  status:        string;
  erp_system?:   string | null;
  go_live_date?: string | null;
  client_id:     string;
  created_at:    string;
}

interface Client {
  id:           string;
  company_name: string;
}

// ---------------------------------------------------------------------------
// ProjectCard
// ---------------------------------------------------------------------------

function ProjectCard({ project, client }: { project: Project; client?: Client }) {
  return (
    <Link
      to={`/projects/${project.id}`}
      className="card p-5 flex flex-col gap-3 hover:ring-1 hover:ring-blue/30 transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-display text-sm font-semibold text-ink group-hover:text-blue transition-colors leading-snug">
            {project.name}
          </p>
          {client && (
            <p className="text-[11px] font-mono text-muted mt-0.5 flex items-center gap-1">
              <Building2 size={10} />
              {client.company_name}
            </p>
          )}
        </div>
        <Badge variant={project.status as never} size="xs" />
      </div>

      <div className="flex items-center gap-4 text-[11px] font-mono text-muted">
        {project.erp_system && (
          <span>{project.erp_system}</span>
        )}
        {project.go_live_date && (
          <span className="flex items-center gap-1">
            <Calendar size={9} />
            {new Date(project.go_live_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
        )}
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// New project form
// ---------------------------------------------------------------------------

interface NewProjectForm {
  name:          string;
  erp_system:    string;
  go_live_date:  string;
  client_id:     string;
}

// Sentinel value for the "+ Add new client…" option in the client <select>,
// distinguishable from any real client_id (a UUID).
const ADD_CLIENT_OPTION = '__add_client__';

// Display labels for ERP_SYSTEMS (docs/NEXT-PHASE-SCOPING.md §3) — the enum
// values themselves are sourced from @veridox/types so this list can't drift
// from public.erp_system, but the raw values ('sap_s4hana', 'oracle_ebs', …)
// aren't fit to show directly. netsuite is the only ERP with a rule library
// behind it today, so it's the only selectable option; the rest are shown
// disabled so the ERP-agnostic architecture is legible without letting
// anyone create a project with no rule library behind it.
const ERP_SYSTEM_LABELS: Record<ErpSystem, string> = {
  sap_s4hana:             'SAP S/4HANA',
  sap_ecc:                'SAP ECC',
  oracle_fusion:          'Oracle Fusion Cloud',
  oracle_ebs:             'Oracle E-Business Suite',
  microsoft_dynamics365:  'Microsoft Dynamics 365',
  netsuite:               'NetSuite',
  workday:                'Workday',
  other:                  'Other',
};

function NewProjectPanel({
  clients,
  onSubmit,
  onCreateClient,
  onCancel,
}: {
  clients:        Client[];
  onSubmit:       (form: NewProjectForm) => Promise<void>;
  onCreateClient: (name: string) => Promise<Client>;
  onCancel:       () => void;
}) {
  // client_id intentionally starts unset, even once clients have loaded —
  // see the removed auto-select effect's replacement note below. The
  // "Select a client…" placeholder option makes that visible in the UI
  // instead of silently landing on an arbitrary existing client.
  const [form, setForm]     = useState<NewProjectForm>({ name: '', erp_system: 'netsuite', go_live_date: '', client_id: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  // Inline client creation — shown automatically when the tenant has no
  // clients yet (nothing to select), or on demand via "+ Add new client…"
  // in the dropdown. No standalone client-management UI exists yet (see
  // CLAUDE.md flag / PR notes), so this is the only way to add a client
  // without leaving the New Project flow.
  const [addingClient, setAddingClient]       = useState(clients.length === 0);
  const [newClientName, setNewClientName]     = useState('');
  const [clientSaving, setClientSaving]       = useState(false);
  const [clientErr, setClientErr]             = useState('');

  // There used to be an effect here that auto-selected clients[0] the
  // moment clients finished loading with form.client_id still unset. That
  // silent default was the actual bug behind "every new project shows
  // client ACME": a user who only typed a client's name into the Project
  // Name field (never touching "+ Add new client…") would submit with
  // client_id already quietly pointing at the tenant's first existing
  // client — confirmed in prod, every differently-named project pointed
  // at the one and only client, ACME. Removed; client_id now stays ''
  // until the user actively picks or adds one, and the placeholder
  // <option> below plus handleSubmit's existing "Select or add a client
  // first" check (which this empty value now actually reaches) make that
  // a required, visible choice instead of an invisible default.

  const handleAddClient = async () => {
    if (!newClientName.trim()) { setClientErr('Client name is required'); return; }
    setClientSaving(true);
    setClientErr('');
    try {
      const created = await onCreateClient(newClientName.trim());
      setForm((f) => ({ ...f, client_id: created.id }));
      setNewClientName('');
      setAddingClient(false);
    } catch (e) {
      setClientErr(e instanceof Error ? e.message : 'Failed to create client');
    } finally {
      setClientSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setErr('Project name is required'); return; }
    if (!form.client_id) { setErr('Select or add a client first'); return; }
    setSaving(true);
    setErr('');
    try {
      await onSubmit(form);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-5 space-y-4 max-w-lg">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">New Project</h3>
        <button onClick={onCancel} className="btn-ghost text-xs text-muted">Cancel</button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="label mb-1.5">Project Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Acme NetSuite Go-Live"
            className="input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label mb-1.5">ERP System</label>
            <select
              value={form.erp_system}
              onChange={(e) => setForm((f) => ({ ...f, erp_system: e.target.value }))}
              className="select text-sm"
            >
              {ERP_SYSTEMS.map((value) => (
                <option key={value} value={value} disabled={value !== 'netsuite'}>
                  {ERP_SYSTEM_LABELS[value]}{value !== 'netsuite' ? ' (coming soon)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label mb-1.5">Go-Live Date</label>
            <input
              type="date"
              value={form.go_live_date}
              onChange={(e) => setForm((f) => ({ ...f, go_live_date: e.target.value }))}
              className="input text-sm"
            />
          </div>
        </div>

        <div>
          <label className="label mb-1.5">Client *</label>

          {!addingClient ? (
            <select
              value={form.client_id}
              onChange={(e) => {
                if (e.target.value === ADD_CLIENT_OPTION) { setAddingClient(true); return; }
                setForm((f) => ({ ...f, client_id: e.target.value }));
              }}
              className="select"
            >
              <option value="" disabled>Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
              <option value={ADD_CLIENT_OPTION}>+ Add new client…</option>
            </select>
          ) : (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-gray/60 border border-[rgba(8,17,31,0.06)]">
              <div className="flex-1 space-y-2">
                {clients.length === 0 && (
                  <p className="text-xs text-muted">
                    No clients yet — add one to create your first project.
                  </p>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="Client / company name"
                    className="input text-sm flex-1"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleAddClient(); } }}
                  />
                  <button
                    onClick={() => void handleAddClient()}
                    disabled={clientSaving}
                    className="btn-secondary text-sm shrink-0"
                  >
                    {clientSaving ? 'Adding…' : 'Add'}
                  </button>
                  {clients.length > 0 && (
                    <button
                      onClick={() => { setAddingClient(false); setNewClientName(''); setClientErr(''); }}
                      className="btn-icon text-muted shrink-0"
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {clientErr && (
                  <div className="flex items-center gap-2 text-xs text-danger">
                    <AlertCircle size={12} /> {clientErr}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <AlertCircle size={13} /> {err}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-[rgba(8,17,31,0.06)]">
        <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button onClick={handleSubmit} disabled={saving} className="btn-primary text-sm">
          {saving ? 'Creating…' : 'Create Project'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProjectsPage
// ---------------------------------------------------------------------------

export function ProjectsPage() {
  const { user, devHeaders } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients]   = useState<Client[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showNew, setShowNew]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [projs, cls] = await Promise.all([
          projectsApi.list(user.tenantId, devHeaders) as Promise<Project[]>,
          clientsApi.list(user.tenantId, devHeaders) as Promise<Client[]>,
        ]);
        if (!cancelled) {
          setProjects(projs);
          setClients(cls);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load projects');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (form: NewProjectForm) => {
    const created = await projectsApi.create({
      ...form,
      tenant_id:  user.tenantId,
      created_by: user.userId,
      status:     'active',
    }, devHeaders) as Project;
    setProjects((prev) => [created, ...prev]);
    setShowNew(false);
    navigate(`/projects/${created.id}`);
  };

  // Inline client creation from the New Project form. tenantId comes from
  // the authenticated session (user.tenantId), never from the form itself.
  // POST /clients returns the raw inserted row (`name`, not `company_name`
  // — that alias only exists on the GET /clients list query), so normalize
  // it here to keep the local `clients` state consistent with what a
  // subsequent list fetch would return.
  const handleCreateClient = async (name: string): Promise<Client> => {
    const created = await clientsApi.create({ tenantId: user.tenantId, name }, devHeaders) as { id: string; name: string };
    const client: Client = { id: created.id, company_name: created.name };
    setClients((prev) => [...prev, client]);
    return client;
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Projects</h1>
          {!loading && (
            <p className="text-sm text-muted mt-1">
              {projects.length} project{projects.length !== 1 ? 's' : ''} for {user.fullName.split(' ')[0]}
            </p>
          )}
        </div>
        {!showNew && (
          <button onClick={() => setShowNew(true)} className="btn-primary">
            <Plus size={15} /> New Project
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-6">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {showNew && (
        <div className="mb-6">
          <NewProjectPanel
            clients={clients}
            onSubmit={handleCreate}
            onCreateClient={handleCreateClient}
            onCancel={() => setShowNew(false)}
          />
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
          {[1,2,3].map((i) => <div key={i} className="card h-28 bg-gray/60" />)}
        </div>
      ) : projects.length === 0 ? (
        <div className="empty-state py-16">
          <FolderOpen size={32} className="text-muted/30 mb-3 mx-auto" />
          <p className="text-sm text-muted">No projects yet.</p>
          <button onClick={() => setShowNew(true)} className="btn-secondary mt-4">
            <Plus size={14} /> Create First Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              client={clients.find((c) => c.id === p.client_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
